import { useEffect, useState } from 'react';
import { ApiClient } from '../api/client';
import type { GameSummary } from '../api/types';
import { StandaloneGames } from '../components/StandaloneGames';
import { useAuth } from '../auth/AuthContext';
import { config } from '../config';
import { useI18n } from '../i18n';

const api = new ApiClient({ baseUrl: config.apiBaseUrl });

export function HomePage() {
  const { t } = useI18n();
  const auth = useAuth();
  const [games, setGames] = useState<GameSummary[] | null>(null);
  const [error, setError] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    const ac = new AbortController();
    setError(false);
    api
      .listGames(ac.signal)
      .then((r) => setGames(r.items))
      .catch(() => {
        if (!ac.signal.aborted) setError(true);
      });
    return () => ac.abort();
  }, [reloadKey]);

  const rpsGame = games?.find((g) => g.game_key === 'rps');
  const rpsFormats = rpsGame?.variants.filter((v) => v.published).length ?? 4;

  return (
    <>
      <section className="hero">
        <div className="stack">
          <span className="eyebrow">{t('hero.eyebrow')}</span>
          <h1 className="hero__title">{t('hero.headline')}</h1>
          <p className="hero__body">{t('hero.body')}</p>
          <div className="hero__actions">
            <a className="btn btn--primary" href="#games">
              {t('hero.cta')}
            </a>
            <a className="btn btn--ghost" href="#games">
              {t('hero.secondary')}
            </a>
          </div>
          <span className="hero__hint">{t('hero.ctaHint')}</span>
          {auth.enabled && !auth.user ? <span className="hero__hint">{t('auth.optional')}</span> : null}
        </div>
        <div className="hero__art hero__art--portal" aria-hidden="true">
          <span>五</span>
          <span>⚄</span>
          <span>✊</span>
        </div>
      </section>

      <section className="portal-strip" aria-label={t('portal.stats')}>
        <div>
          <strong>3</strong>
          <span>{t('portal.stats.games')}</span>
        </div>
        <div>
          <strong>{rpsFormats}</strong>
          <span>{t('portal.stats.rpsFormats')}</span>
        </div>
        <div>
          <strong>{t('portal.stats.noSignup.value')}</strong>
          <span>{t('portal.stats.noSignup')}</span>
        </div>
      </section>

      <section aria-labelledby="games-title" id="games">
        <div className="section-heading">
          <div>
            <span className="eyebrow">{t('games.eyebrow')}</span>
            <h2 className="section-title" id="games-title">
              {t('games.title')}
            </h2>
          </div>
          <p className="muted">{t('games.intro')}</p>
        </div>

        <StandaloneGames rpsGame={rpsGame} />

        {error ? (
          <div className="notice notice--error spread">
            <span>{t('games.error')}</span>
            <button type="button" className="btn btn--sm" onClick={() => setReloadKey((k) => k + 1)}>
              {t('games.retry')}
            </button>
          </div>
        ) : games === null ? (
          <p className="faint">{t('games.loading')}</p>
        ) : games.length === 0 ? (
          <p className="faint">{t('games.empty')}</p>
        ) : (
          <p className="faint">{t('games.liveReady')}</p>
        )}
      </section>

      <section aria-labelledby="why-title">
        <div className="section-heading">
          <div>
            <span className="eyebrow">{t('why.eyebrow')}</span>
            <h2 className="section-title" id="why-title">
              {t('why.title')}
            </h2>
          </div>
        </div>
        <div className="grid-3">
          <article className="card stack stack--tight">
            <h3 className="game-card__title">{t('why.fair.title')}</h3>
            <p className="muted">{t('why.fair.body')}</p>
          </article>
          <article className="card stack stack--tight">
            <h3 className="game-card__title">{t('why.anon.title')}</h3>
            <p className="muted">{t('why.anon.body')}</p>
          </article>
          <article className="card stack stack--tight">
            <h3 className="game-card__title">{t('why.lobbies.title')}</h3>
            <p className="muted">{t('why.lobbies.body')}</p>
          </article>
        </div>
      </section>
    </>
  );
}
